import React from 'react';
import styled from 'styled-components';

// KUON studio logo, taken from the header of kuon.com.ua: a square mark (KU/ON) and the
// KUON wordmark whose O is an open ring. The wordmark follows currentColor; the mark keeps
// KUON's accent. With `drawn` toggling false → true, the ring draws itself like on kuon.com.ua.

export const KUON_ACCENT = '#C8351C';
export const KUON_ON_ACCENT = '#FBFAF7';

export const MARK_SQUARE = 'M0 0H100V100H0Z';
export const MARK_LETTERS = 'M19.68 47V18.99H24.76V31.87H25.1L36.04 18.99H42.22L31.39 31.57L42.32 47H36.21L27.86 35.01L24.76 38.67V47ZM75.31 18.99H80.36V37.28Q80.36 40.29 78.95 42.57Q77.54 44.86 74.99 46.14Q72.43 47.43 69.01 47.43Q65.59 47.43 63.03 46.14Q60.47 44.86 59.06 42.57Q57.64 40.29 57.64 37.28V18.99H62.71V36.87Q62.71 38.63 63.48 39.99Q64.25 41.34 65.67 42.12Q67.09 42.9 69.01 42.9Q70.93 42.9 72.34 42.12Q73.75 41.34 74.53 39.99Q75.31 38.63 75.31 36.87ZM31.01 80.98Q27.34 80.98 24.42 79.27Q21.5 77.55 19.81 74.34Q18.13 71.13 18.13 66.61Q18.13 62.08 19.81 58.86Q21.5 55.64 24.42 53.92Q27.34 52.21 31.01 52.21Q34.69 52.21 37.59 53.92Q40.48 55.64 42.18 58.86Q43.87 62.08 43.87 66.61Q43.87 71.13 42.18 74.34Q40.48 77.55 37.59 79.27Q34.69 80.98 31.01 80.98ZM31.01 76.43Q33.28 76.43 35.03 75.3Q36.78 74.17 37.78 71.98Q38.77 69.79 38.77 66.61Q38.77 63.42 37.78 61.22Q36.78 59.02 35.03 57.89Q33.28 56.76 31.01 56.76Q28.73 56.76 26.98 57.89Q25.22 59.02 24.22 61.22Q23.23 63.42 23.23 66.61Q23.23 69.79 24.22 71.98Q25.22 74.17 26.98 75.3Q28.73 76.43 31.01 76.43ZM80.52 52.59V80.6H75.97L62.8 61.52H62.55V80.6H57.48V52.59H62.03L75.2 71.67H75.45V52.59Z';
export const WORDMARK = 'M0 100V0H18.12V45.97H19.33L58.39 0H80.47L41.81 44.9L80.81 100H58.99L29.19 57.18L18.12 70.27V100ZM146.2 0H164.25V65.3Q164.25 76.04 159.22 84.19Q154.18 92.35 145.06 96.95Q135.93 101.54 123.71 101.54Q111.5 101.54 102.37 96.95Q93.24 92.35 88.18 84.19Q83.11 76.04 83.11 65.3V0H101.23V63.83Q101.23 70.13 103.98 74.97Q106.73 79.8 111.8 82.58Q116.87 85.37 123.71 85.37Q130.56 85.37 135.59 82.58Q140.63 79.8 143.41 74.97Q146.2 70.13 146.2 63.83ZM365.01 0V100H348.77L301.72 31.88H300.85V100H282.73V0H298.97L346.02 68.12H346.89V0Z';
export const RING_ARC = 213.6604145023426;
export const RING = { cx: 219.65, cy: 50, r: 41.08, strokeWidth: 18.04, arc: RING_ARC, gap: 258.1132524189374 };

// The mark's letters as separate glyphs K, U, O, N (the O keeps its counter: outer + inner).
const MARK_PARTS = MARK_LETTERS.split('ZM').map((part, index, parts) =>
  `${index > 0 ? 'M' : ''}${part}${index < parts.length - 1 ? 'Z' : ''}`
);
export const MARK_GLYPHS = [MARK_PARTS[0], MARK_PARTS[1], MARK_PARTS[2] + MARK_PARTS[3], MARK_PARTS[4]];

// The wordmark path holds K, U and N as separate subpaths (the O is the ring), so the
// letters can be animated one by one.
export const WORDMARK_LETTERS = WORDMARK.split('ZM').map((part, index, parts) =>
  `${index > 0 ? 'M' : ''}${part}${index < parts.length - 1 ? 'Z' : ''}`
);

// The ring draws itself (stroke-dashoffset) and can be turned via --kuon-ring-turn on any
// ancestor; both are CSS transitions, so a new turn simply continues from the current angle.
const Ring = styled.circle`
  transform-box: fill-box;
  transform-origin: center;
  transform: rotate(var(--kuon-ring-turn, 0turn));
  stroke-dashoffset: ${(p) => (p.$drawn ? 0 : RING_ARC)};
  transition:
    stroke-dashoffset 0.9s cubic-bezier(0.65, 0, 0.35, 1) var(--kuon-ring-delay, 0.15s),
    transform 1.1s cubic-bezier(0.65, 0, 0.35, 1);

  @media (prefers-reduced-motion: reduce) {
    stroke-dashoffset: 0;
    transform: none;
    transition: none;
  }
`;

export function KuonMark({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true" focusable="false" style={{ flexShrink: 0 }}>
      <path d={MARK_SQUARE} fill={KUON_ACCENT} />
      <path d={MARK_LETTERS} fill={KUON_ON_ACCENT} />
    </svg>
  );
}

const Lockup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  font-size: ${(p) => p.$size}px;
  line-height: 1;

  /* Entrance: the mark pops in, then the wordmark wipes in from the left, then the ring draws */
  .kuon-mark {
    transform-origin: 50% 60%;
    transition:
      transform 800ms cubic-bezier(0.34, 1.56, 0.64, 1) 100ms,
      opacity 300ms ease 100ms;
  }

  .kuon-word {
    clip-path: inset(-10% -4% -10% 0);
    transition: clip-path 900ms cubic-bezier(0.22, 1, 0.36, 1) 300ms;
  }

  &[data-revealed='false'] .kuon-mark {
    opacity: 0;
    transform: scale(0.4) rotate(-16deg);
  }

  &[data-revealed='false'] .kuon-word {
    clip-path: inset(-10% 100% -10% 0);
  }

  @media (prefers-reduced-motion: reduce) {
    .kuon-mark,
    .kuon-word {
      transform: none;
      clip-path: none;
      transition: opacity 300ms ease;
    }

    &[data-revealed='false'] .kuon-mark,
    &[data-revealed='false'] .kuon-word {
      transform: none;
      clip-path: none;
      opacity: 0;
    }
  }
`;

// Proportions match the kuon.com.ua header: mark 1.4em tall, wordmark 0.85em, gap 0.5em.
export function KuonLogo({ size = 20, drawn = true, revealed = true }) {
  return (
    <Lockup $size={size} role="img" aria-label="KUON" data-revealed={revealed}>
      <svg className="kuon-mark" viewBox="0 0 100 100" style={{ height: '1.4em', width: 'auto', flexShrink: 0 }} aria-hidden="true" focusable="false">
        <path d={MARK_SQUARE} fill={KUON_ACCENT} />
        <path d={MARK_LETTERS} fill={KUON_ON_ACCENT} />
      </svg>
      <svg className="kuon-word" viewBox="0 -0.6 365.01 101.2" style={{ height: '0.85em', width: 'auto', overflow: 'visible' }} aria-hidden="true" focusable="false">
        <path d={WORDMARK} fill="currentColor" />
        <Ring
          cx="219.65"
          cy="50"
          r="41.08"
          fill="none"
          stroke="currentColor"
          strokeWidth="18.04"
          strokeLinecap="round"
          strokeDasharray="213.6604145023426 258.1132524189374"
          $drawn={drawn}
        />
      </svg>
    </Lockup>
  );
}

export default KuonLogo;
